import React from 'react';
import classnames from 'classnames';
import { compose } from 'redux';
import { connectMediaStore } from '../../_hocs/withOwnReduxStore';
import { withTranslation, WithTranslation } from 'react-i18next';
import SelectedItemsPanel from '../SelectedItemsPanel';
import GroupOperationButton from '../GroupOperationButton';
import GallectionListMenu from '../GallectionListMenu';
import { getFolderIdsDisabledForDeviationCopy } from '../../helpers';
import {
  copyDeviations,
  moveDeviations,
  removeDeviations,
} from '../../actions';
import withCurrentGallection, {
  WithCurrentGallectionProps,
} from '../../_hocs/withCurrentGallection';
import withSelectedDeviations, {
  WithSelectedDeviationsProps,
} from '../../_hocs/withSelectedDeviations';
import withGallections, {
  WithGallectionsProps,
} from '../../_hocs/withGallections';
import { useManageDeviations } from '../../_hocs/withManageDeviations';
import LabelAsMenu from '../LabelAsMenu';
import withCurrentSubfolder, {
  WithCurrentSubfolderProps,
} from '../../_hocs/withCurrentSubfolder';

import manageableSelectedItemsPanelStyles from './ManageableSelectedItemsPanel.scss';

export interface Props {}

const enhance = compose(
  withGallections,
  withCurrentGallection,
  withCurrentSubfolder,
  withSelectedDeviations,
  connectMediaStore(null, {
    copyDeviations,
    moveDeviations,
    removeDeviations,
  }),
  withTranslation()
);

export const ManageableSelectedItemsPanel: React.FC<
  Props &
    WithCurrentGallectionProps &
    WithCurrentSubfolderProps &
    WithGallectionsProps &
    WithSelectedDeviationsProps &
    WithTranslation
> = ({
  t,
  gallections,
  currentGallection: currentTopLevelGallection,
  currentSubfolder,
  selectedDeviations,
  selectedDeviationIds,
}) => {
  const {
    isCopyToSupportedGlobally,
    isMoveToSupportedGlobally,
    isCopyToSupportedInThisFolder,
    isMoveToSupportedInThisFolder,
    isRemoveSupportedInThisFolder,
    isLabelAsSupportedInThisFolder,
    moveTo,
    copyTo,
    remove,
  } = useManageDeviations(selectedDeviations);

  const showCopyTo = isCopyToSupportedGlobally;
  const showMoveTo = isMoveToSupportedGlobally;
  const showLabelAs = isLabelAsSupportedInThisFolder;
  const isCopyToDisabled = !isCopyToSupportedInThisFolder;
  const isMoveToDisabled = !isMoveToSupportedInThisFolder;
  const isRemoveDisabled = !isRemoveSupportedInThisFolder;

  const currentGallection = currentSubfolder || currentTopLevelGallection;

  return (
    <SelectedItemsPanel
      extras={
        <div>
          {showMoveTo && (
            <GroupOperationButton
              className={
                manageableSelectedItemsPanelStyles['group-operation-button']
              }
              disabled={isMoveToDisabled}
            >
              {t('mediapopup.moveTo')}
              <div
                className={
                  manageableSelectedItemsPanelStyles[
                    'group-operation-menu-wrapper'
                  ]
                }
              >
                <GallectionListMenu onSelect={moveTo} />
              </div>
            </GroupOperationButton>
          )}
          {showCopyTo && (
            <GroupOperationButton
              className={
                manageableSelectedItemsPanelStyles['group-operation-button']
              }
              disabled={isCopyToDisabled}
            >
              {t('mediapopup.copyTo')}
              <div
                className={
                  manageableSelectedItemsPanelStyles[
                    'group-operation-menu-wrapper'
                  ]
                }
              >
                <GallectionListMenu
                  disableFolderIds={getFolderIdsDisabledForDeviationCopy(
                    gallections,
                    currentTopLevelGallection?.folderId
                  )}
                  onSelect={copyTo}
                />
              </div>
            </GroupOperationButton>
          )}
          {showLabelAs && (
            <GroupOperationButton
              className={
                manageableSelectedItemsPanelStyles['group-operation-button']
              }
            >
              {t('mediapopup.labelAs')}
              <div
                className={
                  manageableSelectedItemsPanelStyles[
                    'group-operation-menu-wrapper'
                  ]
                }
              >
                <LabelAsMenu
                  gallection={currentGallection}
                  deviationIds={selectedDeviationIds}
                />
              </div>
            </GroupOperationButton>
          )}
          <GroupOperationButton
            onClick={remove}
            className={classnames(
              manageableSelectedItemsPanelStyles['group-operation-button'],
              manageableSelectedItemsPanelStyles['group-remove']
            )}
            disabled={isRemoveDisabled}
          >
            {t('mediapopup.delete')}
          </GroupOperationButton>
        </div>
      }
    />
  );
};
ManageableSelectedItemsPanel.displayName = 'ManageableSelectedItemsPanel';

export default enhance(
  ManageableSelectedItemsPanel
) as React.ComponentType<Props>;
